<template>
  <b-overlay :show="downloading" spinner small>
    <!-- Identity -->
    <div class="acct-head">
      <div class="acct-avatar">{{ initials }}</div>
      <div class="acct-id">
        <div class="acct-name">{{ user.name || '—' }}</div>
        <div class="acct-meta">
          <span class="fm-mono">{{ user.email }}</span>
          <span v-if="user.organization" class="acct-sep">·</span>
          <span v-if="user.organization">{{ user.organization }}</span>
        </div>
      </div>
      <span v-if="user.role" class="acct-role" :class="user.role === 'admin' ? 'acct-role--admin' : 'acct-role--member'">{{ user.role }}</span>
    </div>

    <!-- Credentials -->
    <div class="acct-section-label">Credentials</div>
    <div class="acct-cred">
      <div class="acct-cred__row">
        <div class="acct-cred__info">
          <div class="acct-cred__title">Auth code</div>
          <div class="acct-cred__desc">Authenticates the CLI and agents on your behalf.</div>
        </div>
        <div class="acct-cred__value">
          <code v-if="showAuthcode" class="acct-code fm-mono">{{ user.authcode }}</code>
          <span v-else class="acct-code acct-code--masked">••••••••••••••••</span>
          <button type="button" class="acct-icon-btn" @click="toggleAuthcode" :title="showAuthcode ? 'Hide' : 'Reveal'">
            <i :class="showAuthcode ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
          </button>
          <button v-if="showAuthcode" type="button" class="acct-icon-btn" :class="{ 'acct-icon-btn--ok': copySuccess }"
                  @click="copyToClipboard(user.authcode)" :title="copySuccess ? 'Copied' : 'Copy'">
            <i class="fa-solid" :class="copySuccess ? 'fa-check' : 'fa-clipboard'"></i>
          </button>
        </div>
      </div>
      <div class="acct-cred__row">
        <div class="acct-cred__info">
          <div class="acct-cred__title">User certificate</div>
          <div class="acct-cred__desc">Downloading a new one invalidates every certificate issued before.</div>
        </div>
        <button type="button" class="fm-btn fm-btn--ghost" @click="confirmDownload">
          <i class="fa-solid fa-download"></i>Download
        </button>
      </div>
    </div>

    <!-- Active sessions -->
    <div class="acct-section-label">Active sessions</div>
    <div v-if="sessions.length === 0" class="acct-empty">
      <i class="fa-regular fa-clock"></i>
      <div class="acct-empty__title">No active sessions</div>
      <div class="acct-empty__sub">Sessions appear here when you connect to a service.</div>
    </div>
    <div v-else class="acct-sessions">
      <b-table
        class="acct-table"
        :items="sessions"
        :fields="sessionFields"
        small
        responsive="sm"
        hover
      >
        <template #cell(Since)="data">
          {{ formatDate(data.item.Since) }}
        </template>
        <template #cell(ConnectionIP)="data">
          <span class="fm-mono">{{ data.item.ConnectionIP }}</span>
        </template>
        <template #cell(ServiceName)="data">
          {{ data.item.ServiceName }}
        </template>
      </b-table>
    </div>

    <b-modal id="confirm-modal" v-model="showConfirmModal" title="Download certificate" hide-footer>
      <p class="mb-0">Downloading a new certificate invalidates every certificate issued before it. Continue?</p>
      <div class="fm-modal-foot">
        <button type="button" class="fm-btn fm-btn--ghost" @click="showConfirmModal = false">Cancel</button>
        <button type="button" class="fm-btn fm-btn--danger" @click="proceedDownload">Download certificate</button>
      </div>
    </b-modal>
  </b-overlay>
</template>

<script>
import {BTable} from 'bootstrap-vue'
import apiClient from '@/http';

export default {
  components: {
    BTable
  },
  computed: {
    initials() {
      const base = (this.user.name || this.user.email || '').trim();
      if (!base) return '—';
      const parts = base.split(/[\s.\-_@]+/).filter(Boolean);
      const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : base.slice(0, 2);
      return letters.toUpperCase();
    }
  },
  data() {
    return {
      user: {
        name: '',
        uid: '',
        email: '',
        role: '',
        organization: '',
        avatarUrl: '',
        authcode: '' // Add authcode to user data
      },
      copySuccess: false,
      showAuthcode: false, // Track whether to show the authcode
      sessions: [], // Array to hold user sessions
      downloading: false,
      sessionFields: [
        {key: 'ServiceName', label: 'Service Name'},
        {key: 'ConnectionIP', label: 'Connected IP'},
        {key: 'Since', label: 'Connected Since'}
      ],
      showConfirmModal: false,
    };
  },
  methods: {
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.copySuccess = true;
        setTimeout(() => {
          this.copySuccess = false;
        }, 1000);
        this.$bvToast.toast('Authcode copied to clipboard', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (err) {
        this.$bvToast.toast('Failed to copy authcode', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }

    },

    async fetchUserDetails() {
      try {
        const response = await apiClient.get('/user');
        this.user.name = response.data.Name;
        this.user.email = response.data.Email;
        this.user.role = response.data.Role;
        this.user.uid = response.data.UserId;
        this.user.organization = response.data.Organization.Name;
        this.user.avatarUrl = response.data.avatarUrl || 'img/faces/face-0.jpg'; // Use default if no avatar URL
      } catch (error) {
        console.error('Failed to fetch user details:', error);
      }
    },
    confirmDownload() {
      this.showConfirmModal = true; // Open the confirmation modal
    },
    async proceedDownload() {
      this.showConfirmModal = false; // Close the modal
      await this.downloadCertificates(); // Proceed with downloading certificates
    },
    async downloadCertificates() {
      try {
        this.downloading = true
        const response = await apiClient.get(`/user/certificates`, {responseType: 'blob'});
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `user_certificate.pem`); // Adjust file name as needed
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloading = false
      } catch (error) {
        this.downloading = false
        console.error('Failed to download certificates:', error);
        this.showNotification('Error downloading certificates', 'danger');
      }
    },
    async fetchUserSessions() {
      try {
        const response = await apiClient.get('/sessions');
        this.sessions = response.data;
      } catch (error) {
        console.error('Failed to fetch user sessions:', error);
      }
    },
    async toggleAuthcode() {
      try {
        const response = await apiClient.head('/user');
        // read X-User-Code from response headers
        this.user.authcode = response.headers['x-user-code'] ? response.headers['x-user-code'] : response.headers['X-User-Code'];
        console.log('Authcode:', response.headers);
        this.showAuthcode = !this.showAuthcode;
      } catch (error) {
        console.error('Failed to fetch user details:', error);
      }

    },
    formatDate(dateString) {
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      };
      return new Date(dateString).toLocaleString(undefined, options);
    }
  },
  mounted() {
    this.fetchUserDetails();
    this.fetchUserSessions();
  }
}
</script>

<style scoped>
/* Identity header */
.acct-head {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 22px;
  border-bottom: 1px solid var(--fm-border);
  margin-bottom: 26px;
}
.acct-avatar {
  width: 56px;
  height: 56px;
  flex: 0 0 auto;
  border-radius: 12px;
  background: var(--fm-slate-800);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.acct-id { min-width: 0; flex: 1; }
.acct-name { font-size: 20px; font-weight: 600; letter-spacing: -0.01em; color: var(--fm-text); }
.acct-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-size: 13px;
  color: var(--fm-text-secondary);
}
.acct-sep { color: var(--fm-slate-300); }
.acct-role {
  flex: 0 0 auto;
  height: 24px;
  display: inline-flex;
  align-items: center;
  padding: 0 11px;
  border-radius: var(--fm-r-pill);
  font-size: 11.5px;
  font-weight: 500;
  text-transform: capitalize;
}
.acct-role--admin { background: var(--fm-accent-soft); color: var(--fm-accent); }
.acct-role--member { background: var(--fm-slate-100); color: var(--fm-slate-600); }

/* Section labels */
.acct-section-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--fm-text-muted);
  margin: 26px 0 10px;
}
.acct-section-label:first-of-type { margin-top: 0; }

/* Credentials */
.acct-cred {
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-r-card);
  overflow: hidden;
}
.acct-cred__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-bottom: 1px solid var(--fm-slate-100);
}
.acct-cred__row:last-child { border-bottom: none; }
.acct-cred__title { font-size: 14px; font-weight: 500; color: var(--fm-text); }
.acct-cred__desc { font-size: 12.5px; color: var(--fm-text-muted); margin-top: 2px; }
.acct-cred__value { display: flex; align-items: center; gap: 8px; flex: 0 0 auto; }
.acct-code {
  font-size: 13px;
  color: var(--fm-slate-700);
  background: var(--fm-slate-50);
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-r-control);
  padding: 6px 10px;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.acct-code--masked { letter-spacing: 2px; color: var(--fm-text-muted); }
.acct-icon-btn {
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  border: 1px solid var(--fm-border);
  background: #fff;
  border-radius: var(--fm-r-control);
  color: var(--fm-slate-500);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color .12s ease, color .12s ease;
}
.acct-icon-btn:hover { background: var(--fm-slate-100); color: var(--fm-slate-700); }
.acct-icon-btn--ok { color: var(--fm-success); border-color: var(--fm-success); }

/* Sessions */
.acct-table { margin-bottom: 0; }
.acct-empty {
  border: 1px dashed var(--fm-border);
  border-radius: var(--fm-r-card);
  padding: 40px 24px;
  text-align: center;
}
.acct-empty i { font-size: 22px; color: var(--fm-slate-300); }
.acct-empty__title { margin-top: 10px; font-size: 14px; font-weight: 500; color: var(--fm-text-secondary); }
.acct-empty__sub { margin-top: 2px; font-size: 12.5px; color: var(--fm-text-muted); }

@media (max-width: 640px) {
  .acct-cred__row { flex-direction: column; align-items: flex-start; }
  .acct-code { max-width: 160px; }
}
</style>
